import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import FooterNav from '../components/ui/footer-nav';
import NavMenu from '../components/ui/nav-menu';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Search, ChevronRight, Loader2, FileX,
  Users, Check, Briefcase, Trophy, Truck, Banknote
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AllWork = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter');
  
  const [updates, setUpdates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingDone, setMarkingDone] = useState(null);
  
  const getInitialTab = () => {
    if (initialFilter === 'win-work') return 'leads';
    if (initialFilter === 'deliver-work') return 'updates';
    return 'all';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [updatesRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/updates/my`),
        axios.get(`${API_URL}/leads/my`)
      ]);
      setUpdates(updatesRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDone = async (e, updateId) => {
    e.stopPropagation();
    setMarkingDone(updateId);
    try {
      await axios.put(`${API_URL}/updates/${updateId}`, { status: 'Done' });
      setUpdates(prev => prev.map(u => u.id === updateId ? { ...u, status: 'Done' } : u));
      toast.success('Marked as Done!');
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setMarkingDone(null);
    }
  };

  const counts = useMemo(() => ({
    leads: leads.length,
    updates: updates.length,
    claims: 0
  }), [leads, updates]);

  const stats = useMemo(() => ({
    total: updates.length + leads.length,
    newCount: updates.filter(u => u.status === 'New').length,
    inProgress: updates.filter(u => u.status === 'In Progress').length,
    done: updates.filter(u => u.status === 'Done').length,
    highUrgency: updates.filter(u => u.urgency === 'High').length,
  }), [updates, leads]);

  const displayItems = useMemo(() => {
    let updateItems = updates.map(u => ({ ...u, itemType: 'update' }));
    let leadItems = leads.map(l => ({ ...l, itemType: 'lead' }));
    
    let items = activeTab === 'updates' ? updateItems 
              : activeTab === 'leads' ? leadItems 
              : activeTab === 'claims' ? [] // Claims not yet implemented
              : [...updateItems, ...leadItems];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        (item.job_name?.toLowerCase().includes(query)) ||
        (item.update_text?.toLowerCase().includes(query)) ||
        (item.summary_text?.toLowerCase().includes(query)) ||
        (item.site_location?.toLowerCase().includes(query))
      );
    }
    
    if (statusFilter !== 'all') {
      items = items.filter(item => item.itemType === 'lead' || item.status === statusFilter);
    }
    
    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [updates, leads, activeTab, searchQuery, statusFilter]);

  const hasActiveFilters = statusFilter !== 'all' || searchQuery;
  const clearFilters = () => { setSearchQuery(''); setStatusFilter('all'); };

  const getStatusClass = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-amber-100 text-amber-700';
      case 'Done': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header - Dark with orange strip */}
      <header className="bg-[#1F2A37] text-white px-4 py-3 safe-area-top relative">
        <div className="h-1 bg-orange-500 absolute top-0 left-0 right-0"></div>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Briefcase className="w-7 h-7 text-orange-400" />
            <div>
              <h1 className="text-xl font-bold">All Work</h1>
              <p className="text-orange-400 text-xs font-semibold uppercase tracking-wide">ALL WORK</p>
            </div>
          </div>
          <NavMenu />
        </div>
      </header>

      <main className="p-4 max-w-lg mx-auto">
        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-slate-200 mb-4 overflow-hidden">
          {/* Search Bar */}
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search jobs, leads, updates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 bg-slate-50 border-slate-200 text-sm"
              />
            </div>
          </div>

          {/* Stage Filters - WIN / DELIVER / GET PAID */}
          <div className="flex border-b border-slate-100">
            {[
              { id: 'all', label: 'All', color: 'slate', icon: Briefcase },
              { id: 'win', label: 'Win', color: 'emerald', icon: Trophy },
              { id: 'deliver', label: 'Deliver', color: 'blue', icon: Truck },
              { id: 'paid', label: 'Get Paid', color: 'orange', icon: Banknote },
            ].map((stage) => {
              const Icon = stage.icon;
              const isActive = (stage.id === 'all' && activeTab === 'all') ||
                (stage.id === 'win' && activeTab === 'leads') ||
                (stage.id === 'deliver' && activeTab === 'updates') ||
                (stage.id === 'paid' && activeTab === 'claims');
              
              return (
                <button
                  key={stage.id}
                  onClick={() => setActiveTab(stage.id === 'win' ? 'leads' : stage.id === 'deliver' ? 'updates' : stage.id === 'paid' ? 'claims' : 'all')}
                  className={`flex-1 flex flex-col items-center gap-1 px-2 py-2.5 text-xs font-semibold transition-colors ${
                    isActive
                      ? stage.color === 'emerald' ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50/50'
                      : stage.color === 'blue' ? 'text-blue-600 border-b-2 border-blue-500 bg-blue-50/50'
                      : stage.color === 'orange' ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50/50'
                      : 'text-slate-800 border-b-2 border-slate-800 bg-slate-50'
                      : 'text-slate-400 hover:text-slate-600 border-b-2 border-transparent'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${
                    isActive
                      ? stage.color === 'emerald' ? 'text-emerald-500'
                      : stage.color === 'blue' ? 'text-blue-500'
                      : stage.color === 'orange' ? 'text-orange-500'
                      : 'text-slate-700'
                      : 'text-slate-300'
                  }`} />
                  <span>{stage.label}</span>
                </button>
              );
            })}
          </div>

          {/* Quick Status Filters */}
          <div className="p-2 flex gap-2 overflow-x-auto">
            {['all', 'New', 'In Progress', 'Done'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  statusFilter === status
                    ? status === 'New' ? 'bg-blue-500 text-white'
                    : status === 'In Progress' ? 'bg-amber-500 text-white'
                    : status === 'Done' ? 'bg-green-500 text-white'
                    : 'bg-slate-700 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {status === 'all' ? 'All Status' : status}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : displayItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <FileX className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-600">{hasActiveFilters ? 'No results' : 'Nothing yet'}</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="mt-4 text-sm text-blue-600">Clear filters</button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {displayItems.map((item) => (
              item.itemType === 'lead' ? (
                <Card
                  key={`lead-${item.id}`}
                  onClick={() => navigate(`/tradie/lead/${item.id}`)}
                  className="p-3 border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-slate-800 text-sm">{item.entry_type_display || 'Lead'}</span>
                    </div>
                    <span className="text-xs font-mono text-emerald-600">#{item.reference_id}</span>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-1 mb-1">{item.summary_text}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{format(new Date(item.created_at), 'MMM d')}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Card>
              ) : (
                <Card
                  key={`update-${item.id}`}
                  onClick={() => navigate(`/tradie/update/${item.id}`)}
                  className="p-3 border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className="font-semibold text-slate-800 text-sm">{item.job_name || 'Untitled'}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${getStatusClass(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-1 mb-1">{item.update_text}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{format(new Date(item.created_at), 'MMM d')}</span>
                    <div className="flex items-center gap-2">
                      {item.status !== 'Done' && (
                        <button
                          onClick={(e) => handleMarkDone(e, item.id)}
                          disabled={markingDone === item.id}
                          className="flex items-center gap-1 px-2 py-0.5 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs font-medium"
                        >
                          {markingDone === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Done
                        </button>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                </Card>
              )
            ))}
          </div>
        )}
      </main>

      <FooterNav counts={counts} />
    </div>
  );
};

export default AllWork;
